import classNames from 'classnames';
import React, { useContext } from 'react';
import {
    MaterialIdsType,
    type IListStyleMaterial,
    type ITextLayerProps,
} from '@meitu/saas-editor-core/es/version-types';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { ListStyleStruct } from '@/types';
import { ListStyleSelector } from '@/widgets/list-style-selector';
import { TextPanelContext } from '../../context';
import { useStore } from '../../store';
import { Tracker } from '../../utils';
import '@/styles/shared.less';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';

export const ListStyle: React.FC = () => {
    const { layerId, recordId, isIntl, listStyles } = useContext(TextPanelContext)!;
    const editor = useEditor();

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const materialIds = layer.materialIds;
        const textListMaterials = materialIds?.[MaterialIdsType.TEXT_LIST] || [];
        const textListMaterialId = textListMaterials.length > 0 ? textListMaterials[0] : '';

        const layerData = layer.data as ITextLayerProps;
        const { shapeExtension, listStyleMaterial } = layerData;

        return {
            shapeExtension,
            textListMaterialId,
            listStyleMaterial,
        };
    });
    const shapeExtensionCache = useStore((state) => state.shapeExtensionCache);

    const shapeExtension = props?.shapeExtension || shapeExtensionCache;
    const textListMaterialId = props ? props.textListMaterialId : '';
    const listStyleMaterial = props ? props.listStyleMaterial : undefined;

    const disableListStyle = !!shapeExtension;

    const handleSetListStyle = (data: ListStyleStruct) => {
        if (data.id === textListMaterialId) return;

        editor.getInstance(TextLayerExtension).applyListText(layerId, data);

        Tracker.setListText({
            recordId,
            stageType: editor.stage.getType(),
            listStyle: data.style,
        });
    };

    const handleCancelListStyle = () => {
        editor.getInstance(TextLayerExtension).cancelListText(layerId);
    };

    const handleListStyleOpenChange = (visible: boolean) => {
        popoverWidgetRef.current = visible;
    };

    const handleUpdateListStyleConfig = (config: IListStyleMaterial, options: { writeHistory: boolean }) => {
        editor.getInstance(TextLayerExtension).updateListStyleMaterial(layerId, config, options);
    };

    return (
        <div className={classNames('list-style', { intl: isIntl })}>
            <ListStyleSelector
                disabled={disableListStyle}
                list={listStyles}
                activeId={textListMaterialId}
                activeConfig={listStyleMaterial}
                onCancelListStyle={handleCancelListStyle}
                onSetListStyle={handleSetListStyle}
                onUpdateConfig={handleUpdateListStyleConfig}
                onOpenChange={handleListStyleOpenChange}
            />
        </div>
    );
};
